import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  CheckCircle2,
  AlertCircle,
  Store,
  Link,
  Unlink,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { redeemShopifyConnectId } from "@/lib/api";
import { api } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function ShopifyStoreConnection() {
  const { user } = useAuth();
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [newShopDomain, setNewShopDomain] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Get shop from URL if present (Shopify embedded app context)
  const urlParams = new URLSearchParams(window.location.search);
  const currentShop = urlParams.get('shop');

  useEffect(() => {
    fetchConnectedStores();
  }, []);

  const fetchConnectedStores = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/shopify-store/list");
      const data = response.data;
      if (data.success) {
        setStores(data.data.stores || []);
      }
    } catch (err) {
      console.error('Failed to fetch stores:', err);
      setError('Failed to load connected stores');
    } finally {
      setLoading(false);
    }
  };

  const connectStore = async (shopDomain) => {
    try {
      setConnecting(true);
      setError(null);

      const response = await api.post("/api/shopify-store/connect", { shop: shopDomain });
      const data = response.data;

      setSuccess(data.message);
      setNewShopDomain('');
      await fetchConnectedStores();

    } catch (err) {
      setError(err.message);
    } finally {
      setConnecting(false);
    }
  };

  const disconnectStore = async (shopDomain) => {
    try {
      const response = await api.post("/api/shopify-store/disconnect", { shop: shopDomain });
      const data = response.data;

      setSuccess('Store disconnected successfully');
      await fetchConnectedStores();

    } catch (err) {
      setError(err.message);
    }
  };

  const handleConnectCurrent = () => {
    if (currentShop) {
      connectStore(currentShop);
    }
  };

  const handleConnectNew = async (e) => {
    e.preventDefault();
    const id = newShopDomain.trim();
    if (!id) return;

    setConnecting(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await redeemShopifyConnectId(id);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || "Failed to link store with this ID");
      }
      setSuccess("Store connected successfully with Connect ID.");
      setNewShopDomain("");
      await fetchConnectedStores();
    } catch (err) {
      setError(err.message || "Failed to link store");
    } finally {
      setConnecting(false);
    }
  };

  const isCurrentShopConnected = currentShop && stores.some(
    store => store.shop_domain === currentShop && store.is_active
  );

  return (
    <div className="space-y-6">
      {/* Current Shopify Store (if in Shopify context) */}
      {currentShop && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Current Shopify Store
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">{currentShop}</p>
                <p className="text-sm text-muted-foreground">
                  {isCurrentShopConnected ? 'Connected to RADAR' : 'Not connected to RADAR'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isCurrentShopConnected ? (
                  <>
                    <Badge variant="success" className="text-xs">
                      <CheckCircle2 className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => disconnectStore(currentShop)}
                    >
                      <Unlink className="w-3.5 h-3.5 mr-1" />
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={handleConnectCurrent}
                    disabled={connecting}
                    className="bg-cta hover:bg-cta/90"
                  >
                    {connecting ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                    ) : (
                      <Link className="w-3.5 h-3.5 mr-1" />
                    )}
                    Connect Store
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Connected Stores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Store className="w-5 h-5" />
              Connected Stores
            </span>
            <Badge variant="secondary">
              {stores.filter(s => s.is_active).length} connected
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
              <span className="ml-2">Loading stores...</span>
            </div>
          ) : stores.length === 0 ? (
            <div className="text-center py-8">
              <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium mb-2">No stores connected yet</h3>
              <p className="text-sm text-muted-foreground">
                Connect your Shopify store to start tracking competitors
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {stores.map((store) => (
                <div
                  key={store.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div>
                    <div className="font-medium">{store.shop_domain}</div>
                    <div className="text-xs text-muted-foreground">
                      Connected {new Date(store.connected_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {store.is_active ? (
                      <>
                        <Badge variant="success" className="text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disconnectStore(store.shop_domain)}
                        >
                          <Unlink className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Badge variant="secondary" className="text-xs">
                          Disconnected
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => connectStore(store.shop_domain)}
                          disabled={connecting}
                        >
                          <Link className="w-3.5 h-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect New Store via 32-character ID */}
      <Card>
        <CardHeader>
          <CardTitle>Connect New Store</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleConnectNew} className="space-y-4">
            <div>
              <Label htmlFor="shopDomain">Shopify Connect ID</Label>
              <Input
                id="shopDomain"
                type="text"
                placeholder="32-character ID from the Shopify app"
                value={newShopDomain}
                onChange={(e) => setNewShopDomain(e.target.value)}
                disabled={connecting}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Paste the 32-character Connect ID generated in the Radar Shopify app Settings.
              </p>
            </div>

            <Button
              type="submit"
              disabled={connecting || !newShopDomain.trim()}
            >
              {connecting ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <Link className="w-3.5 h-3.5 mr-1" />
              )}
              Connect Store
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Messages */}
      {error && (
        <div className="flex items-start gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertCircle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-destructive">Connection Error</p>
            <p className="text-sm text-destructive/80">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="flex items-start gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-800">Success</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Help Text */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <h3 className="font-medium mb-2">How Store Connection Works</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Connect your Shopify store to enable competitor tracking</li>
            <li>• RADAR will automatically detect competitors based on your products</li>
            <li>• You can connect multiple stores to one RADAR account</li>
            <li>• Competitor detection runs in the background once connected</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}